/**
 * 旅行 (Trip) API サービス
 *
 * フルアクセス権限を持つユーザーのみ使用可能
 */

import { apiClient } from '@/api/client'
import type {
  ConquestMap,
  Trip,
  TripCreatePayload,
  TripDetail,
  TripPosterCopy,
  TripRoute,
  TripsOnDate,
  TripStatus,
  TripStoryResult,
  TripTimeline,
  TripUpdatePayload,
} from '@/api/types/trip'

/** 旅行一覧を新しい順に取得 */
export const getTrips = async (status?: TripStatus, limit = 100): Promise<Trip[]> => {
  const params: Record<string, string | number> = { limit }
  if (status) params.status = status
  const res = await apiClient.get<Trip[]>('/trips', { params })
  return res.data
}

/** 旅行を手動作成 */
export const createTrip = async (payload: TripCreatePayload): Promise<Trip> => {
  const res = await apiClient.post<Trip>('/trips', payload)
  return res.data
}

/** 旅行の詳細（統計込み） */
export const getTrip = async (tripId: number): Promise<TripDetail> => {
  const res = await apiClient.get<TripDetail>(`/trips/${tripId}`)
  return res.data
}

/** 旅行を編集 */
export const updateTrip = async (tripId: number, payload: TripUpdatePayload): Promise<Trip> => {
  const res = await apiClient.patch<Trip>(`/trips/${tripId}`, payload)
  return res.data
}

/** 旅行を削除（訪問・写真そのものは消えない） */
export const deleteTrip = async (tripId: number): Promise<void> => {
  await apiClient.delete(`/trips/${tripId}`)
}

/** 日ごとのストーリー（訪問・写真・メモ） */
export const getTripTimeline = async (tripId: number): Promise<TripTimeline> => {
  const res = await apiClient.get<TripTimeline>(`/trips/${tripId}/timeline`)
  return res.data
}

/** GPS 軌跡（間引き済み） */
export const getTripRoute = async (tripId: number): Promise<TripRoute> => {
  const res = await apiClient.get<TripRoute>(`/trips/${tripId}/route`)
  return res.data
}

/** AI に紀行文を書かせて保存 */
export const generateTripStory = async (tripId: number): Promise<TripStoryResult> => {
  const res = await apiClient.post<TripStoryResult>(`/trips/${tripId}/story`)
  return res.data
}

/** 記録と写真を AI に見せてポスターのコピーと写真選定を作らせる */
export const generateTripPoster = async (tripId: number): Promise<TripPosterCopy> => {
  const res = await apiClient.post<TripPosterCopy>(`/trips/${tripId}/poster`)
  return res.data
}

/** 訪問を旅の記録から除外 */
export const excludeTripVisit = async (tripId: number, visitId: number): Promise<void> => {
  await apiClient.post(`/trips/${tripId}/exclude-visit/${visitId}`)
}

/** 訪問の除外を解除 */
export const includeTripVisit = async (tripId: number, visitId: number): Promise<void> => {
  await apiClient.delete(`/trips/${tripId}/exclude-visit/${visitId}`)
}

/** その日にかかっている旅と、過去の同じ日の旅（YYYY-MM-DD, JST） */
export const getTripsOnDate = async (day: string): Promise<TripsOnDate> => {
  const res = await apiClient.get<TripsOnDate>('/trips/on-date', { params: { day } })
  return res.data
}

/** 訪問済みの都道府県・市区町村の集計（制覇マップ用） */
export const getConquestMap = async (): Promise<ConquestMap> => {
  const res = await apiClient.get<ConquestMap>('/trips/conquest')
  return res.data
}
